import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface Achievement {
  id: string;
  name: string;
  description: string;
  criteria: any;
  points: number;
}

interface UserAchievement {
  id: string;
  achievement_id: string;
  earned_at: string;
  achievement: Achievement;
}

export const useAchievements = (profileUserId?: string) => {
  const { user } = useAuth();
  const [earnedAchievements, setEarnedAchievements] = useState<UserAchievement[]>([]);
  const [availableAchievements, setAvailableAchievements] = useState<Achievement[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const targetUserId = profileUserId || user?.id;

  useEffect(() => {
    if (!targetUserId) {
      setIsLoading(false);
      return;
    }

    fetchAchievements();
  }, [targetUserId]);

  const fetchAchievements = async () => {
    if (!targetUserId) return;

    try {
      setIsLoading(true);

      // Fetch user's earned achievements
      const { data: userAchievements, error: userError } = await supabase
        .from('user_achievements')
        .select(`
          *,
          achievement:achievements(*)
        `)
        .eq('user_id', targetUserId)
        .order('earned_at', { ascending: false });

      if (userError) throw userError;

      // Fetch all available achievements
      const { data: allAchievements, error: allError } = await supabase
        .from('achievements')
        .select('*')
        .order('points', { ascending: true });

      if (allError) throw allError;

      const earnedIds = userAchievements?.map(ua => ua.achievement_id) || [];
      const available = allAchievements?.filter(a => !earnedIds.includes(a.id)) || [];

      setEarnedAchievements(userAchievements || []);
      setAvailableAchievements(available);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkAndAwardAchievements = async (userStats: any) => {
    if (!user) return;

    try {
      // This would contain logic to check if user qualifies for any new achievements
      // based on their current stats (runs, miles, trails, etc.)
      // For now, we'll just refetch to see if any new achievements were awarded
      await fetchAchievements();
    } catch (error) {
      console.error('Error checking achievements:', error);
    }
  };

  return {
    earnedAchievements,
    availableAchievements,
    isLoading,
    refetch: fetchAchievements,
    checkAchievements: checkAndAwardAchievements
  };
};
